import { admin2 } from '@/api/instance'

const baseUrl = (shopId) => `/${shopId}/couponXigogo`
const clientBaseUrl = (shopId) => `/${shopId}/shopCustomModule/xigogo`

/**
 * 取得洗GOGO匠折價券列表 admin.couponXigogo.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} [params.name] - 優惠券名稱（模糊搜尋）
 * @param {number} [params.start=0] - 查詢起始
 * @param {number} [params.limit] - 查詢筆數
 * @returns {import('@/api/instance').Api2Response<Array>} 洗GOGO匠折價券列表
 */
export const GetXigogoCoupon = async ({
  shopId,
  name,
  start,
  limit,
}) => {
  return await admin2({
    method: 'GET',
    url: baseUrl(shopId),
    params: {
      name,
      start,
      limit,
    },
  })
}

/**
 * 取得洗GOGO匠折價券數量 admin.couponXigogo.count
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} [params.name] - 優惠券名稱（模糊搜尋）
 * @returns {import('@/api/instance').Api2Response<{count:number}>} 折價券數量
 */
export const GetXigogoCouponCount = async ({
  shopId,
  name,
}) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/count`,
    params: {
      name,
    },
  })
}

/**
 * 取得單筆洗GOGO匠折價券 admin.couponXigogo.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} params.id - 折價券 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 折價券資料
 */
export const FindXigogoCoupon = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 新增洗GOGO匠折價券 admin.couponXigogo.create
 * @param {Object} params - 建立參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} params.name - 優惠券名稱
 * @param {string} [params.notice] - 店家備註
 * @param {Object} params.extraSetting - 券設定，內含 `xigogoConfig`（折扣／到期／限定門市商品）
 * @param {string} [params.image] - 圖片 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 新建之折價券
 */
export const CreateXigogoCoupon = async ({
  shopId,
  name,
  notice,
  extraSetting,
  image,
}) => {
  return await admin2({
    method: 'POST',
    url: baseUrl(shopId),
    data: {
      name,
      notice,
      extraSetting,
      image,
    },
  })
}

/**
 * 更新洗GOGO匠折價券 admin.couponXigogo.update
 * @param {Object} params - 更新參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} params.id - 折價券 ID
 * @param {string} [params.name] - 優惠券名稱
 * @param {string} [params.notice] - 店家備註
 * @param {Object} [params.extraSetting] - 券設定，內含 `xigogoConfig`
 * @param {string} [params.image] - 圖片 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 更新後之折價券
 */
export const UpdateXigogoCoupon = async ({
  shopId,
  id,
  name,
  notice,
  extraSetting,
  image,
}) => {
  return await admin2({
    method: 'PUT',
    url: `${baseUrl(shopId)}/${id}`,
    data: {
      name,
      notice,
      extraSetting,
      image,
    },
  })
}

/**
 * 刪除洗GOGO匠折價券 admin.couponXigogo.destroy
 * @param {Object} params - 刪除參數
 * @param {string} params.shopId - 商店 ID
 * @param {string} params.id - 折價券 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 已刪除之折價券
 */
export const DeleteXigogoCoupon = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'DELETE',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 取得洗GOGO匠門市／商品清單（券設定「限定門市／商品」選項來源與已下架比對）admin.couponXigogo.storeProductList
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店 ID
 * @returns {import('@/api/instance').Api2Response<{storeList:Array, serviceList:Array}>} 門市與商品／服務清單
 * @example
 * // 回傳格式
 * {
 *   storeList: [{ store_id: 'S001', name: '信義店' }],
 *   serviceList: [{ service_id: 'P001', name: '洗車', price: 300 }]
 * }
 */
export const GetXigogoStoreProductList = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/storeProductList`,
  })
}

/**
 * 新增洗GOGO匠串接（產生 clientId／clientSecret，明文僅回傳一次）admin.shopCustomModule.createXigogoClient
 * @param {Object} params - 建立參數
 * @param {string} params.shopId - 商店 ID
 * @param {boolean} [params.enabled] - 是否啟用
 * @param {Object} [params.config={}] - 模組設定
 * @returns {import('@/api/instance').Api2Response<Object>} 串接模組，含一次性 `clientId`／`clientSecret`
 */
export const CreateXigogoClient = async ({
  shopId,
  enabled,
  config,
}) => {
  return await admin2({
    method: 'POST',
    url: clientBaseUrl(shopId),
    data: {
      enabled,
      config,
    },
  })
}

/**
 * 更新洗GOGO匠串接（啟用／停用）admin.shopCustomModule.updateXigogoClient
 * @param {Object} params - 更新參數
 * @param {string} params.shopId - 商店 ID
 * @param {boolean} [params.enabled] - 是否啟用
 * @param {Object} [params.config={}] - 模組設定
 * @returns {import('@/api/instance').Api2Response<Object>} 更新後之串接模組
 */
export const UpdateXigogoClient = async ({
  shopId,
  enabled,
  config,
}) => {
  return await admin2({
    method: 'PUT',
    url: clientBaseUrl(shopId),
    data: {
      enabled,
      config,
    },
  })
}
